/**
 * Benefit Categories — seeded: HOUSE_ALLOWANCE, TRANSPORT_ALLOWANCE, CAR_ALLOWANCE, AIRTIME, RESPONSIBILITY, OTHER.
 * Authority: HR_PAYROLL_MANAGE (write) / HR_PAYROLL_VIEW (read)
 */
import { useState } from "react";
import { Plus, Gift } from "lucide-react";
import { useAuth } from "#/context/AuthProvider";
import { payrollApi } from "../api";
import type { BenefitCategory, BenefitCategoryRequest } from "../types";
import { useAsyncData, useMutation } from "../hooks";
import {
  Btn, Card, EmptyState, PageHeader, PayrollError, PayrollLoading,
  Table, TBody, TD, TH, THead, TR,
} from "../components/PayrollUI";

const emptyForm: BenefitCategoryRequest = { code: "", name: "", description: "", active: true };

export default function PayrollBenefitCategoriesPage() {
  const { hasRole, hasPermission } = useAuth();
  const isAdmin = hasRole("SUPER_ADMIN") || hasRole("ADMIN");
  const canManage = isAdmin || hasPermission("HR_PAYROLL_MANAGE");

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<BenefitCategory | null>(null);
  const [form, setForm] = useState<BenefitCategoryRequest>(emptyForm);

  const { data, loading, error, reload } = useAsyncData(() => payrollApi.listBenefitCategories(), []);
  const create = useMutation((payload: BenefitCategoryRequest) => payrollApi.createBenefitCategory(payload));
  const update = useMutation((payload: { id: number; body: Partial<BenefitCategoryRequest> }) => payrollApi.updateBenefitCategory(payload.id, payload.body));

  function openCreate() {
    setEditing(null);
    setForm(emptyForm);
    setShowForm(true);
  }

  function openEdit(cat: BenefitCategory) {
    setEditing(cat);
    setForm({ code: cat.code, name: cat.name, description: cat.description ?? "", active: cat.active });
    setShowForm(true);
  }

  async function save() {
    if (editing) await update.mutate({ id: editing.id, body: form });
    else await create.mutate(form);
    setShowForm(false);
    reload();
  }

  const inp = "w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100";
  const busy = create.loading || update.loading;
  const saveError = create.error || update.error;

  return (
    <div className="space-y-5">
      <PageHeader
        breadcrumb="Finance · Setup"
        title="Benefit Categories"
        actions={canManage && !showForm && <Btn variant="primary" icon={Plus} onClick={openCreate}>New Category</Btn>}
      />

      {showForm && canManage && (
        <Card>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block text-xs font-semibold text-slate-500">
              Code
              <input className={`mt-1 ${inp}`} value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))} placeholder="e.g. CAR_ALLOWANCE" disabled={!!editing} />
            </label>
            <label className="block text-xs font-semibold text-slate-500">
              Name
              <input className={`mt-1 ${inp}`} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Car Allowance" />
            </label>
            <label className="block text-xs font-semibold text-slate-500 sm:col-span-2">
              Description
              <input className={`mt-1 ${inp}`} value={form.description ?? ""} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
            </label>
            <label className="flex items-center gap-2 text-xs font-semibold text-slate-500">
              <input type="checkbox" checked={form.active ?? true} onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))} />
              Active
            </label>
          </div>
          {saveError && <p className="mt-3 text-sm text-red-600">{saveError}</p>}
          <div className="mt-4 flex justify-end gap-2">
            <Btn onClick={() => setShowForm(false)}>Cancel</Btn>
            <Btn variant="primary" loading={busy} loadingText="Saving…" disabled={!form.code || !form.name} onClick={save}>
              {editing ? "Save Changes" : "Create Category"}
            </Btn>
          </div>
        </Card>
      )}

      {loading && <PayrollLoading label="Loading benefit categories…" />}
      {error && <PayrollError message={error} onRetry={reload} />}

      {!loading && data && (
        <Table>
          <THead><TH>Code</TH><TH>Name</TH><TH>Description</TH><TH>Status</TH><TH right>Actions</TH></THead>
          <TBody>
            {data.length === 0 ? (
              <tr><td colSpan={5}><EmptyState icon={Gift} title="No benefit categories" /></td></tr>
            ) : data.map((c) => (
              <TR key={c.id}>
                <TD className="font-mono text-xs font-semibold text-indigo-700">{c.code}</TD>
                <TD className="font-semibold text-slate-800">{c.name}</TD>
                <TD muted>{c.description ?? "—"}</TD>
                <TD>{c.active
                  ? <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700">Active</span>
                  : <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-500">Inactive</span>}
                </TD>
                <TD right>{canManage && <Btn size="sm" onClick={() => openEdit(c)}>Edit</Btn>}</TD>
              </TR>
            ))}
          </TBody>
        </Table>
      )}
    </div>
  );
}
